import axiosInstance from "@/helpers/axios";

export default {
  namespaced: true,
  state: () => ({
    poll: null,
    existingResponse: null,
    answers: {},
    currentStepIndex: 0,
    isLoadingPoll: false,
    isSaving: false,
    isSubmitting: false,
    isSubmitted: false,
    thankYouText: "",
    loadError: null,
    saveError: null,
  }),
  getters: {
    poll: (state) => state.poll,
    answers: (state) => state.answers,
    currentStepIndex: (state) => state.currentStepIndex,
    totalSteps: (state) => (state.poll?.sections?.length ?? 0),
    currentSection: (state) => {
      if (!state.poll?.sections) return null;
      return state.poll.sections[state.currentStepIndex] ?? null;
    },
    isLoadingPoll: (state) => state.isLoadingPoll,
    isSaving: (state) => state.isSaving,
    isSubmitting: (state) => state.isSubmitting,
    isSubmitted: (state) => state.isSubmitted,
    thankYouText: (state) => state.thankYouText,
    loadError: (state) => state.loadError,
    saveError: (state) => state.saveError,
  },
  mutations: {
    SET_POLL(state, poll) {
      state.poll = poll;
    },
    SET_EXISTING_RESPONSE(state, response) {
      state.existingResponse = response;
    },
    SET_ANSWER(state, { key, value }) {
      state.answers = { ...state.answers, [key]: value };
    },
    SET_ANSWERS(state, answers) {
      state.answers = { ...answers };
    },
    SET_CURRENT_STEP(state, index) {
      state.currentStepIndex = index;
    },
    SET_LOADING(state, val) {
      state.isLoadingPoll = val;
    },
    SET_SAVING(state, val) {
      state.isSaving = val;
    },
    SET_SUBMITTING(state, val) {
      state.isSubmitting = val;
    },
    SET_SUBMITTED(state, val) {
      state.isSubmitted = val;
    },
    SET_THANK_YOU(state, text) {
      state.thankYouText = text;
    },
    SET_LOAD_ERROR(state, err) {
      state.loadError = err;
    },
    SET_SAVE_ERROR(state, err) {
      state.saveError = err;
    },
    RESET(state) {
      state.poll = null;
      state.existingResponse = null;
      state.answers = {};
      state.currentStepIndex = 0;
      state.isLoadingPoll = false;
      state.isSaving = false;
      state.isSubmitting = false;
      state.isSubmitted = false;
      state.thankYouText = "";
      state.loadError = null;
      state.saveError = null;
    },
  },
  actions: {
    async loadPoll({ commit }, pollId) {
      commit("RESET");
      commit("SET_LOADING", true);
      try {
        const [pollRes, responseRes] = await Promise.allSettled([
          axiosInstance.get(`/polls/${pollId}`),
          axiosInstance.get(`/polls/${pollId}/response`),
        ]);

        if (pollRes.status === "rejected") {
          const status = pollRes.reason?.response?.status;
          commit(
            "SET_LOAD_ERROR",
            status === 404 ? "not_found" : "error"
          );
          return;
        }

        commit("SET_POLL", pollRes.value.data);

        if (
          responseRes.status === "fulfilled" &&
          responseRes.value?.data
        ) {
          const existingResponse = responseRes.value.data;
          commit("SET_EXISTING_RESPONSE", existingResponse);
          if (existingResponse.answers) {
            commit("SET_ANSWERS", existingResponse.answers);
          }
          if (existingResponse.completed) {
            commit("SET_SUBMITTED", true);
            commit(
              "SET_THANK_YOU",
              pollRes.value.data.thank_you_text || ""
            );
          }
        }
      } catch (e) {
        commit("SET_LOAD_ERROR", "error");
      } finally {
        commit("SET_LOADING", false);
      }
    },

    setAnswer({ commit }, { key, value }) {
      commit("SET_ANSWER", { key, value });
    },

    async saveStep({ commit, state }, { pollId, stepIndex, isFinal }) {
      commit("SET_SAVING", true);
      commit("SET_SAVE_ERROR", null);
      try {
        const section = state.poll?.sections?.[stepIndex];
        await axiosInstance.post(`/polls/${pollId}/response/step`, {
          step_id: section?.id,
          answers: state.answers,
          is_final: isFinal || false,
        });
      } catch (e) {
        const message =
          e?.response?.data?.message || "שגיאה בשמירת השלב. אנא נסו שוב.";
        commit("SET_SAVE_ERROR", message);
        throw e;
      } finally {
        commit("SET_SAVING", false);
      }
    },

    async submitPoll({ commit, state }, pollId) {
      commit("SET_SUBMITTING", true);
      commit("SET_SAVE_ERROR", null);
      try {
        const { data } = await axiosInstance.post(
          `/polls/${pollId}/response/submit`,
          { answers: state.answers }
        );
        commit("SET_SUBMITTED", true);
        commit(
          "SET_THANK_YOU",
          data.thank_you_text ||
            state.poll?.thank_you_text ||
            "תודה על מילוי הסקר!"
        );
      } catch (e) {
        const message =
          e?.response?.data?.message || "שגיאה בשליחת הסקר. אנא נסו שוב.";
        commit("SET_SAVE_ERROR", message);
        throw e;
      } finally {
        commit("SET_SUBMITTING", false);
      }
    },

    goToStep({ commit }, index) {
      commit("SET_CURRENT_STEP", index);
    },
  },
};
